import { ODSInlineNotification, type ODSNotificationMode } from '@telekom-ods/react-ui-kit';
import { type ReactNode, useState, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { setNotify, clearNotify } from './notificationBridge.js';
import NotificationContext from './notificationContext.js';
import './NotificationProvider.css';

let idCounter = 1;

interface Message {
  id: number;
  message: string;
  type: ODSNotificationMode;
  title: string;
}

interface PushPayload {
  message: string;
  type?: ODSNotificationMode;
  title?: string;
}

interface NotificationReturn {
  id: number;
  dismiss: () => void;
}

interface NotificationProviderProps {
  children: ReactNode;
}

const createMessageFilter =
  (targetId: number) =>
  (m: Message): boolean =>
    m.id !== targetId;

const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const intl = useIntl();
  const [messages, setMessages] = useState<Message[]>([]);

  const push = useCallback((payload: PushPayload): NotificationReturn => {
    const id = idCounter++;
    const timeout = 15 * 1000; // 15 seconds
    const msg: Message = {
      id,
      message: payload.message,
      type: payload.type ?? ('info' as ODSNotificationMode),
      title: payload.title ?? '',
    };

    setMessages(prev => [...prev, msg]);

    const dismissTime = setTimeout(() => {
      setMessages(prev => prev.filter(createMessageFilter(id)));
    }, timeout);

    return {
      id,
      dismiss: () => {
        clearTimeout(dismissTime);
        setMessages(prev => prev.filter(createMessageFilter(id)));
      },
    };
  }, []);

  const remove = useCallback((id: number): void => {
    setMessages(prev => prev.filter(createMessageFilter(id)));
  }, []);

  useEffect(() => {
    setNotify((payload: unknown) =>
      typeof payload === 'string' ? push({ message: payload }) : push(payload as PushPayload)
    );
    return () => {
      clearNotify();
    };
  }, [push]);

  const contextValue = useMemo(() => ({ push, remove, messages }), [push, remove, messages]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <div className='notificationsDiv'>
        {messages.map(m => (
          <ODSInlineNotification
            key={m.id}
            mode={m.type}
            title={m.title && intl.formatMessage({ id: m.title })}
            text={m.message && intl.formatMessage({ id: m.message })}
            onClose={() => {
              remove(m.id);
            }}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
